import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Reusable "Related records" tab — the native replacement for NetSuite's
// Related Records subtab (task #55). Drop it onto any native record page:
//
//   <RelatedRecords recordType="item" id={item.id} />
//
// Reads GET /api/related/:recordType/:id (allow-listed on the server; the
// declarative registry in packages/related decides what each record type is
// linked to). Renders one collapsible card per relationship group with an
// exact count and a few preview rows; rows link through to the related
// record's page where a Run route exists. Sibling of <RecordHistory /> —
// together they are the two subtabs NetSuite bolts onto every record.
//
// Styling: scoped .nit-* classes from NativeItems.css (per the NativeItems
// patterns) plus the .nit-rel-* additions at the bottom of that file.

const fmtCell = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  // Timestamps render as local date-times; plain dates stay as-is.
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return new Date(s).toLocaleString();
  return s.length > 80 ? `${s.slice(0, 77)}…` : s;
};

// Which row fields make a useful one-line preview, in priority order. Raw
// uuid/fk plumbing is skipped — the linked record's page shows the detail.
const PREVIEW_FIELDS = [
  'wo_number', 'order_ref', 'external_order_id', 'order_number', 'po_number',
  'invoice_number', 'entry_number', 'sku', 'assembly_sku', 'name',
  'display_name', 'company_name', 'customer_name', 'label', 'system',
  'ns_entity_id', 'mode', 'kind', 'status', 'application_status',
  'movement_kind', 'transaction_type', 'source_type', 'source_id',
  'tracking_number', 'external_payment_id', 'line_number', 'quantity',
  'quantity_per', 'amount', 'total_amount', 'city', 'state',
  'transaction_date', 'ship_date', 'entry_date', 'order_date', 'created_at',
];
const MAX_PREVIEW_FIELDS = 5;

function previewEntries(row) {
  const picked = [];
  for (const field of PREVIEW_FIELDS) {
    const rendered = fmtCell(row[field]);
    if (rendered != null) picked.push([field, rendered]);
    if (picked.length >= MAX_PREVIEW_FIELDS) return picked;
  }
  if (picked.length > 0) return picked;
  // Nothing recognizable — fall back to the first few scalar non-id fields.
  for (const [field, value] of Object.entries(row)) {
    if (field === 'id' || field.endsWith('_id') || value == null || typeof value === 'object') continue;
    picked.push([field, fmtCell(value)]);
    if (picked.length >= MAX_PREVIEW_FIELDS) break;
  }
  return picked;
}

// Where a related row can link to a page that exists in Run today.
// Key: `${recordType}.${group.key}` → href builder (null = no link yet).
// Extend this map as native record pages land (orders, WOs, POs, customers).
const LINK_RULES = {
  'item.bom': (row) => row.component_item_id && `/native-items/${encodeURIComponent(row.component_item_id)}`,
  'item.where_used': (row) => row.parent_item_id && `/native-items/${encodeURIComponent(row.parent_item_id)}`,
  'work_order.components': (row) => row.component_item_id && `/native-items/${encodeURIComponent(row.component_item_id)}`,
  'purchase_order.lines': (row) => row.inventory_item_id && `/native-items/${encodeURIComponent(row.inventory_item_id)}`,
  // Record pages (tasks #62/#60)
  'item.po_lines': (row) => row.po_id && `/records/purchase-orders/${encodeURIComponent(row.po_id)}`,
  'sales_order.ns_mirror': (row) => row.id && `/records/sales-orders/${encodeURIComponent(row.id)}`,
};

function RelatedGroup({ recordType, group }) {
  const linkFor = LINK_RULES[`${recordType}.${group.key}`];
  return (
    <details className="nit-rel-group" open={group.count > 0}>
      <summary className="nit-rel-summary">
        <span className="nit-rel-label">{group.label}</span>
        <span className={`nit-tab-count ${group.count === 0 ? 'nit-rel-count-zero' : ''}`}>{group.count}</span>
        {group.table && <code className="nit-rel-table">{group.table}</code>}
      </summary>

      {group.count === 0 && <div className="nit-empty nit-rel-empty">No linked records.</div>}

      {group.rows.length > 0 && (
        <ul className="nit-rel-rows">
          {group.rows.map((row, i) => {
            const href = linkFor ? linkFor(row) : null;
            const cells = previewEntries(row).map(([field, value]) => (
              <span className="nit-rel-cell" key={field}>
                <span className="nit-rel-field">{field.replace(/_/g, ' ')}</span> {value}
              </span>
            ));
            return (
              <li className="nit-rel-row" key={row.id || i}>
                {href ? <Link to={href} className="nit-rel-link">open</Link> : null}
                {cells.length > 0 ? cells : <span className="nit-muted">record {row.id || `#${i + 1}`}</span>}
              </li>
            );
          })}
        </ul>
      )}

      {(group.truncated || group.note) && (
        <p className="nit-caption">
          {group.truncated && `Showing the first ${group.rows.length} of ${group.count}. `}
          {group.note}
        </p>
      )}
    </details>
  );
}

export default function RelatedRecords({ recordType, id, title = 'Related records', limit = 5 }) {
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!recordType || !id) return;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    axios
      .get(`/api/related/${encodeURIComponent(recordType)}/${encodeURIComponent(id)}`, { params: { limit } })
      .then(({ data }) => { if (!cancelled) setState({ data, loading: false, error: null }); })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.response?.data?.error || err.message });
      });
    return () => { cancelled = true; };
  }, [recordType, id, limit]);

  const { data, loading, error } = state;
  const groups = (data && data.groups) || [];
  const linked = groups.reduce((s, g) => s + (g.count || 0), 0);

  return (
    <div className="nit-card">
      <div className="nit-hist-head">
        <h3>{title}</h3>
        {data && <span className="nit-muted">{linked} linked record{linked === 1 ? '' : 's'} across {groups.length} relationships</span>}
      </div>

      {loading && <div className="nit-loading">Loading related records…</div>}
      {error && <div className="nit-error">{error}</div>}

      {!loading && !error && groups.map((g) => (
        <RelatedGroup key={g.key} recordType={recordType} group={g} />
      ))}
    </div>
  );
}
